import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

function ExecutiveSummary() {
  const navigate = useNavigate();

  // Mock data
  const modelInventory = {
    total: 142,
    active: 126,
    retired: 16
  };

  const modelByCategory = {
    labels: ['Credit Risk', 'Market Risk', 'Operational Risk', 'Fraud Detection', 'Other'],
    datasets: [{
      data: [45, 28, 22, 35, 12],
      backgroundColor: ['#4472C4', '#ED7D31', '#A5A5A5', '#FFC000', '#5B9BD5'],
      borderWidth: 2,
      borderColor: '#fff'
    }]
  };

  const modelByStage = {
    labels: ['Development', 'Testing', 'UAT', 'Production', 'Retired'],
    datasets: [{
      label: 'Number of Models',
      data: [18, 24, 15, 69, 16],
      backgroundColor: '#4472C4',
      borderRadius: 4
    }]
  };

  const validationStatus = {
    labels: ['Current', 'Due Soon', 'Overdue'],
    datasets: [{
      data: [85, 42, 15],
      backgroundColor: ['#70AD47', '#FFC000', '#C5504F'],
      borderWidth: 2,
      borderColor: '#fff'
    }]
  };

  const modelsWithFindings = [
    { id: 'M-001', name: 'Credit Risk Model 1', category: 'Credit Risk', stage: 'Production', owner: 'John Doe', findings: 3, high: 1, medium: 2, low: 0 },
    { id: 'M-015', name: 'Fraud Detection V2', category: 'Fraud', stage: 'UAT', owner: 'Jane Smith', findings: 2, high: 0, medium: 1, low: 1 },
    { id: 'M-023', name: 'Market Risk Calculator', category: 'Market', stage: 'Production', owner: 'Bob Wilson', findings: 4, high: 2, medium: 1, low: 1 },
    { id: 'M-087', name: 'Operational Risk Assessment', category: 'Operational', stage: 'Testing', owner: 'Alice Brown', findings: 1, high: 0, medium: 0, low: 1 },
    { id: 'M-102', name: 'Credit Scoring Model', category: 'Credit', stage: 'Production', owner: 'Charlie Davis', findings: 2, high: 0, medium: 2, low: 0 }
  ];

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          boxWidth: 12,
          padding: 10
        }
      }
    }
  };

  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        display: false
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 20
        }
      }
    }
  };

  return (
    <div className="container-fluid">
      {/* Header */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2>
                <i className="bi bi-bar-chart-line me-2"></i>
                Executive Summary
              </h2>
              <p className="text-muted mb-0">Comprehensive Model Risk Management Overview</p>
            </div>
            <button className="btn btn-outline-secondary" onClick={() => navigate('/reports')}>
              <i className="bi bi-arrow-left me-2"></i>
              Quay lại
            </button>
          </div>
        </div>
      </div>

      {/* Top Row - Model Inventory and Charts */}
      <div className="row g-4 mb-4">
        {/* Model Inventory */}
        <div className="col-lg-3">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body text-center">
              <h5 className="card-title text-muted mb-3">Model Inventory</h5>
              <h1 className="display-2 fw-bold text-primary mb-3">{modelInventory.total}</h1>
              <div className="text-start">
                <p className="mb-2">
                  <span className="text-muted">Active Models:</span>
                  <span className="float-end fw-bold text-success">{modelInventory.active}</span>
                </p>
                <p className="mb-0">
                  <span className="text-muted">Retired Models:</span>
                  <span className="float-end fw-bold text-secondary">{modelInventory.retired}</span>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Model by Category */}
        <div className="col-lg-3">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <h5 className="card-title text-muted mb-3 text-center">Model by Category</h5>
              <div style={{ height: '200px' }}>
                <Doughnut data={modelByCategory} options={chartOptions} />
              </div>
            </div>
          </div>
        </div>

        {/* Model by Stage */}
        <div className="col-lg-3">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <h5 className="card-title text-muted mb-3 text-center">Model by Stage</h5>
              <div style={{ height: '200px' }}>
                <Bar data={modelByStage} options={barChartOptions} />
              </div>
            </div>
          </div>
        </div>

        {/* Validation Status */}
        <div className="col-lg-3">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <h5 className="card-title text-muted mb-3 text-center">Validation Status</h5>
              <div style={{ height: '200px' }}>
                <Doughnut data={validationStatus} options={chartOptions} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Model by Risk Tier */}
      <div className="row g-4 mb-4">
        <div className="col-12">
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-white border-bottom">
              <h5 className="mb-0">
                <i className="bi bi-thermometer-half me-2"></i>
                Model by Risk Tier
              </h5>
            </div>
            <div className="card-body">
              <div className="table-responsive">
                <table className="table table-bordered mb-0">
                  <thead className="table-light">
                    <tr>
                      <th rowSpan="2" className="align-middle">Category</th>
                      <th colSpan="4" className="text-center bg-light">Inventory Risk</th>
                      <th colSpan="4" className="text-center bg-warning bg-opacity-25">Materially Spread</th>
                    </tr>
                    <tr>
                      <th className="text-center">Credit Risk</th>
                      <th className="text-center">Market Risk</th>
                      <th className="text-center">Fraud</th>
                      <th className="text-center">Total</th>
                      <th className="text-center">High</th>
                      <th className="text-center">Medium</th>
                      <th className="text-center">Low</th>
                      <th className="text-center">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="fw-bold">All Risk</td>
                      <td className="text-center">120</td>
                      <td className="text-center">51</td>
                      <td className="text-center">35</td>
                      <td className="text-center fw-bold">128</td>
                      <td className="text-center bg-danger text-white">27 (54%)</td>
                      <td className="text-center bg-warning">17 (34%)</td>
                      <td className="text-center bg-success text-white">6 (12%)</td>
                      <td className="text-center fw-bold">50</td>
                    </tr>
                    <tr>
                      <td className="fw-bold">Materially High</td>
                      <td className="text-center">85</td>
                      <td className="text-center">38</td>
                      <td className="text-center">22</td>
                      <td className="text-center fw-bold">95</td>
                      <td className="text-center bg-danger text-white">18 (51%)</td>
                      <td className="text-center bg-warning">12 (34%)</td>
                      <td className="text-center bg-success text-white">5 (14%)</td>
                      <td className="text-center fw-bold">35</td>
                    </tr>
                    <tr>
                      <td className="fw-bold">Immaterial Low</td>
                      <td className="text-center">35</td>
                      <td className="text-center">13</td>
                      <td className="text-center">13</td>
                      <td className="text-center fw-bold">33</td>
                      <td className="text-center bg-danger text-white">9 (60%)</td>
                      <td className="text-center bg-warning">5 (33%)</td>
                      <td className="text-center bg-success text-white">1 (7%)</td>
                      <td className="text-center fw-bold">15</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Open Findings and Models with Findings */}
      <div className="row g-4">
        {/* Open Findings Summary */}
        <div className="col-lg-3">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body text-center">
              <h5 className="card-title text-muted mb-3">Open Findings</h5>
              <h1 className="display-2 fw-bold text-danger mb-3">5</h1>
              <div className="d-flex justify-content-around">
                <div>
                  <span className="badge bg-danger fs-5">1</span>
                  <p className="mb-0 mt-2 small">High</p>
                </div>
                <div>
                  <span className="badge bg-warning text-dark fs-5">2</span>
                  <p className="mb-0 mt-2 small">Medium</p>
                </div>
                <div>
                  <span className="badge bg-success fs-5">2</span>
                  <p className="mb-0 mt-2 small">Low</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Models with Findings Table */}
        <div className="col-lg-9">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-header bg-white border-bottom">
              <h5 className="mb-0">
                <i className="bi bi-exclamation-triangle me-2"></i>
                Models with Findings
              </h5>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Model ID</th>
                      <th>Model Name</th>
                      <th>Category</th>
                      <th>Stage</th>
                      <th>Owner</th>
                      <th className="text-center">Total</th>
                      <th className="text-center">High</th>
                      <th className="text-center">Med</th>
                      <th className="text-center">Low</th>
                    </tr>
                  </thead>
                  <tbody>
                    {modelsWithFindings.map((model, index) => (
                      <tr key={index}>
                        <td className="fw-bold">{model.id}</td>
                        <td>{model.name}</td>
                        <td>{model.category}</td>
                        <td>
                          <span className="badge bg-info">{model.stage}</span>
                        </td>
                        <td>{model.owner}</td>
                        <td className="text-center fw-bold">{model.findings}</td>
                        <td className="text-center">
                          {model.high > 0 && <span className="badge bg-danger">{model.high}</span>}
                        </td>
                        <td className="text-center">
                          {model.medium > 0 && <span className="badge bg-warning text-dark">{model.medium}</span>}
                        </td>
                        <td className="text-center">
                          {model.low > 0 && <span className="badge bg-success">{model.low}</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ExecutiveSummary;
